import { SelectInput, TextFieldInput } from 'sharedComponents/Form';
import { yupResolver } from '@hookform/resolvers/yup';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { fieldMinValueMessage, fieldRequiredMessage } from 'locales/formValidationMessages';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { mixed, number, object } from 'yup';
import { useDateContext } from '../utils/DateContext';
import {
  calculateRefreshIntervalValue,
  convertMillisecondsToRefreshTimeValue,
  getTimeUnitFromMilliseconds,
  TIME_UNITS,
} from '../utils/dateConfig';

const TIME_UNITS_OPTIONS = [
  {
    value: TIME_UNITS[0],
    intl: defineMessage({ id: 'FancyDateSelect-RefreshEvery-timeUnitOption-seconds', defaultMessage: 'seconds' }),
  },
  {
    value: TIME_UNITS[1],
    intl: defineMessage({ id: 'FancyDateSelect-RefreshEvery-timeUnitOption-minutes', defaultMessage: 'minutes' }),
  },
  {
    value: TIME_UNITS[2],
    intl: defineMessage({ id: 'FancyDateSelect-RefreshEvery-timeUnitOption-hours', defaultMessage: 'hours' }),
  },
];

const useRefreshEveryFormSchema = () => {
  const intl = useIntl();
  return object().shape({
    timeValue: number()
      .positive()
      .required(() => intl.formatMessage(fieldRequiredMessage))
      .when(['timeUnit'], {
        is: TIME_UNITS[0],
        then: schema => schema.min(5, ({ min }) => intl.formatMessage(fieldMinValueMessage, { min })),
        otherwise: schema => schema.min(1, ({ min }) => intl.formatMessage(fieldMinValueMessage, { min })),
      }),
    timeUnit: mixed().required(() => intl.formatMessage(fieldRequiredMessage)),
  });
};

const RefreshEvery = ({ handleClose }) => {
  const { refreshInterval, setRefreshInterval } = useDateContext();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      timeValue: refreshInterval
        ? convertMillisecondsToRefreshTimeValue({
            value: refreshInterval,
            timeUnit: getTimeUnitFromMilliseconds(refreshInterval),
          })
        : 60,
      timeUnit: refreshInterval ? getTimeUnitFromMilliseconds(refreshInterval) : TIME_UNITS_OPTIONS[0].value,
    },
    resolver: yupResolver(useRefreshEveryFormSchema()),
  });

  const onSubmit = useCallback(
    formData => {
      if (refreshInterval) {
        setRefreshInterval(undefined);
      } else {
        const { timeValue, timeUnit } = formData;
        setRefreshInterval(calculateRefreshIntervalValue({ value: timeValue, timeUnit }));
        handleClose();
      }
    },
    [setRefreshInterval, handleClose, refreshInterval]
  );

  return (
    <Box sx={theme => ({ marginLeft: theme.spacing(1) })}>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        <FormattedMessage id="FancyDateSelect-RefreshEvery-title" defaultMessage="Auto refresh every" />
      </Typography>
      <Box>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, alignItems: 'flex-end' }}
        >
          <TextFieldInput
            type="number"
            fullWidth={false}
            id="timeValueRefreshEvery"
            disabled={!!refreshInterval}
            errors={errors}
            registerProps={register('timeValue')}
          />
          <SelectInput
            control={control}
            id="timeUnit"
            name="timeUnit"
            fullWidth={false}
            error={!!errors['timeUnit']}
            disabled={!!refreshInterval}
          >
            {TIME_UNITS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <FormattedMessage {...option.intl} />
              </MenuItem>
            ))}
          </SelectInput>
          <Button
            size="small"
            type="submit"
            color="primary"
            variant="contained"
            startIcon={refreshInterval ? <StopIcon /> : <PlayIcon />}
          >
            {refreshInterval ? (
              <FormattedMessage id="FancyDateSelect-RefreshEvery-stop-button" defaultMessage="Stop" />
            ) : (
              <FormattedMessage id="FancyDateSelect-RefreshEvery-start-button" defaultMessage="Start" />
            )}
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default RefreshEvery;
